/**
 * AddressR — structured address field (line1/line2/suburb/state/postcode/country).
 * Subfields are defined in types.json#primitives.address.
 */

import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { TextField, Section } from '../../../components/intake/fields';
import { ComboboxField } from '../../../components/intake/ComboboxField';
import type { FieldDef, RendererProps, RefDataOption } from '../types';
import {
  loadRefData,
  getRefDataSync,
  loadCountrySubdivisions,
  getCountrySubdivisionsSync,
} from '../reference-data/loader';
import { resolveLabel, resolveHelpText, deriveFieldName } from '../utils';
import { lookupFieldTooltip } from '../../fieldTooltips';

interface Props extends RendererProps {
  field: FieldDef;
}

const COUNTRY_REF = '/reference-data/countries.json';
const AU_STATES_REF = '/reference-data/au-states.json';

export function AddressR({ field, formId, pdfMode = false, namePrefix, audience }: Props) {
  const { getValues, watch } = useFormContext();
  const baseName = deriveFieldName(field, namePrefix);
  const label = resolveLabel(field, formId, audience);

  const [countries, setCountries] = useState<RefDataOption[]>(
    getRefDataSync(COUNTRY_REF) ?? [],
  );
  const [auStates, setAuStates] = useState<RefDataOption[]>(
    getRefDataSync(AU_STATES_REF) ?? [],
  );
  // Bump on subdivisions load so the country->state branch re-reads the map.
  const [subsLoaded, setSubsLoaded] = useState(false);

  useEffect(() => {
    if (countries.length === 0) loadRefData(COUNTRY_REF).then(setCountries);
    if (auStates.length === 0) loadRefData(AU_STATES_REF).then(setAuStates);
    loadCountrySubdivisions().then(() => setSubsLoaded(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sub = (subfield: string) => `${baseName}.${subfield}`;

  if (pdfMode) {
    const v = getValues(baseName) as Record<string, string> | undefined ?? {};
    const countryLabel = countries.find((c) => c.value === v.country)?.label ?? v.country;
    const stateLabel = auStates.find((s) => s.value === v.state)?.label ?? v.state;
    const lines = [v.line1, v.line2, v.suburb, stateLabel, v.postcode, countryLabel]
      .filter(Boolean)
      .join(', ');
    return (
      <div>
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
        <p className="text-sm text-slate-800 mt-0.5">
          {lines || <span className="text-slate-400 italic">Not provided</span>}
        </p>
      </div>
    );
  }

  // Watch (not getValues) so the state subfield reacts when country changes.
  const selectedCountry = watch(sub('country')) as string | undefined;
  const isAustralia = selectedCountry === 'AU';
  // `subsLoaded` gates the sync read so this recomputes once the map resolves.
  const subdivisions =
    subsLoaded && selectedCountry && !isAustralia
      ? getCountrySubdivisionsSync(selectedCountry) ?? []
      : [];

  return (
    <Section title={label} description={resolveHelpText(field, audience)} tooltip={lookupFieldTooltip(field.canonical_name)}>
      <TextField name={sub('line1')} label="Street address" required={field.required} />
      <TextField name={sub('line2')} label="Street address line 2" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextField name={sub('suburb')} label="Suburb / City" required={field.required} />
        <TextField name={sub('postcode')} label="Postcode / ZIP" required={field.required} />
      </div>
      {/* Country before State (Immi order); the state field adapts to the country. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ComboboxField
          name={sub('country')}
          label="Country"
          options={countries}
          required={field.required}
        />
        {isAustralia ? (
          <ComboboxField
            name={sub('state')}
            label="State / Territory"
            options={auStates}
          />
        ) : subdivisions.length > 0 ? (
          <ComboboxField
            name={sub('state')}
            label="State / Province"
            options={subdivisions}
          />
        ) : (
          <TextField name={sub('state')} label="State / Province" />
        )}
      </div>
    </Section>
  );
}
